import React from 'react';
import { motion } from 'framer-motion';
import { HiXMark, HiArrowDownTray, HiDocumentText } from 'react-icons/hi2';

const STAGE_NAMES = {
  idea: 'Idea Validation',
  research: 'Market Research',
  prototype: 'Prototype Development',
  development: 'Product Development',
  mvp: 'MVP Launch',
};

const SubmissionPreviewModal = ({ isOpen, onClose, submission }) => {
  if (!isOpen || !submission) return null;

  const formatDate = date => {
    if (!date) return 'N/A';
    if (date.toDate) {
      return date.toDate().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleDownloadAttachment = attachment => {
    if (attachment.downloadURL) {
      const link = document.createElement('a');
      link.href = attachment.downloadURL;
      link.download = attachment.name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const renderField = (key, value) => {
    // Skip system/metadata fields
    const systemFields = [
      'id',
      'userId',
      'userEmail',
      'createdAt',
      'updatedAt',
      'attachments',
      'stage',
      'stageName',
      'feedback',
      'clientName',
      'clientEmail',
      'docId',
      'createdByStaffId',
      'createdByStaffEmail',
      'createdByStaffName',
    ];

    if (systemFields.includes(key)) {
      return null;
    }

    // Skip null, undefined, objects, and arrays
    // Note: typeof null === 'object' in JavaScript, so we check for null explicitly
    if (value === null || value === undefined) {
      return null;
    }

    // Skip objects and arrays (only render primitives)
    if (typeof value === 'object' || Array.isArray(value)) {
      return null;
    }

    // Only render string, number, or boolean values
    // Empty strings are allowed (they're valid values)
    if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
      return null;
    }

    // Skip empty strings (optional - remove if you want to show empty fields)
    if (typeof value === 'string' && value.trim() === '') {
      return null;
    }

    const label = key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();

    return (
      <div key={key} className="mb-6">
        <h3 className="mb-2 text-sm font-semibold text-gray-700">{label}</h3>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="whitespace-pre-wrap text-sm text-gray-700">{String(value)}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white shadow-xl"
      >
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-black">
              {STAGE_NAMES[submission.stage] || submission.stageName}
            </h2>
            <p className="text-sm text-gray-600">Submitted on {formatDate(submission.createdAt)}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <HiXMark className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {Object.entries(submission)
            .map(([key, value]) => renderField(key, value))
            .filter(Boolean)}

          {submission.attachments && submission.attachments.length > 0 && (
            <div className="mt-6">
              <h3 className="mb-4 text-sm font-semibold text-gray-700">Attachments</h3>
              <div className="space-y-2">
                {submission.attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4"
                  >
                    <div className="flex items-center gap-3">
                      <HiDocumentText className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{attachment.name}</p>
                        <p className="text-xs text-gray-500">
                          {(attachment.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                    {attachment.downloadURL && (
                      <button
                        onClick={() => handleDownloadAttachment(attachment)}
                        className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                      >
                        <HiArrowDownTray className="h-4 w-4" />
                        Download
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default SubmissionPreviewModal;
